'use client';
import Button from '@/components/Button';
import ChoiceButton from '@/components/ChoiceButton';
import Input from '@/components/Input';
import { Modal } from '@/components/Modal/Modal';
import Tooltip from '@/components/Tooltip';
import ArrowLeftCircle from '@/components/icons/ArrowLeftCircle';
import PlusCircle from '@/components/icons/PlusCircle';
import { NotifsContext } from '@/contexts';
import useNavigate from '@/hooks/useNavigate';
import { AddSteReq, useAddSteMutation } from '@/services/API';
import form from '@/styles/form.module.css';
import { AuthnMethod, EmployerType, IEmployer } from '@/types';
import { getEmptyError, getMinLenError } from '@/types/Input';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';
import { useContext, useEffect, useRef, useState } from 'react';
import styles from './page.module.css';

export default function Page() {
  type SteFormModel = Pick<
    IEmployer,
    | 'name'
    | 'siren'
    | 'tag'
    | 'authn_method'
    | 'employer_type'
    | 'api_organization_id'
  >;
  interface MethodChoice {
    label: string;
    value: AuthnMethod;
  }
  interface EmpTypeChoice {
    label: string;
    value: EmployerType;
  }
  const navigate = useNavigate();
  const sirenRef = useRef<HTMLDivElement>(null);
  const [isDisabled, setIsDisabled] = useState(true);
  const [addSte, { error: err, isLoading }] = useAddSteMutation();
  const [steForm, setSteForm] = useState<SteFormModel>({
    name: '',
    siren: '',
    tag: '',
    authn_method: 'LOCAL',
    employer_type: 'COMPANY',
    api_organization_id: '',
  });
  const [steErr, setSteErr] = useState({
    name: '',
    siren: '',
    tag: '',
    api_organization_id: '',
  });
  const methods: MethodChoice[] = [
    { label: 'Locale', value: 'LOCAL' },
    { label: 'Fédérée', value: 'FEDERATED' },
  ];
  const types: EmpTypeChoice[] = [
    { label: 'Société', value: 'COMPANY' },
    { label: 'Evènement', value: 'EVENT' },
  ];

  const handleSubmit = () => {
    if (steForm) {
      let req = steForm as AddSteReq;
      req = { ...req, fin_ent: 'PF' };

      addSte(req)
        .unwrap()
        .then(() => {
          addToast &&
            addToast({
              toastType: 'success',
              children: 'Confirmation de creation.',
            });
          navigate('');
        })
        .catch(() => {
          addToast &&
            addToast({
              toastType: 'error',
              children: `Une erreur est survenue.
              Rééssayer ultérieurement.`,
            });
        })
        .finally(() => setOpenedModal(false));
    }
  };

  useEffect(() => {
    if (steForm.name !== '' && steForm.siren !== '' && steForm.tag !== '')
      setIsDisabled(false);
    else setIsDisabled(true);
  }, [steForm]);

  useEffect(() => {
    if (err) {
      let fetchErr = err as FetchBaseQueryError;
      if (fetchErr.data && Array.isArray(fetchErr.data)) {
        let scimError = fetchErr.data as Array<{
          error: string;
          error_description: string;
        }>;
        if (scimError[0].error === 'uniqueness') {
          setSteErr({
            ...steErr,
            siren: 'Ce SIREN existe déjà dans la liste des sociétés',
          });
          sirenRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  }, [err]);

  const [openedModal, setOpenedModal] = useState(false);
  const { addToast } = useContext(NotifsContext);

  return (
    <div className={form.content}>
      <Button
        variant="ghost"
        icon={<ArrowLeftCircle />}
        onClick={() => navigate(-1)}
      >
        Retour
      </Button>
      <form className={`${form.form} gap-l`}>
        <h3 className="bigger" ref={sirenRef}>
          Créer une société
        </h3>
        <Input
          value={steForm.name}
          maxLength={50}
          label="Raison sociale *"
          placeholder="SA"
          onChange={(e) => setSteForm({ ...steForm, name: e.target.value })}
          errorMessage={steErr.name}
          onBlur={(e) =>
            setSteErr({ ...steErr, name: getEmptyError(e.target.value) })
          }
        />
        <Input
          label="SIREN *"
          placeholder="123456789"
          helperText="9 chiffres"
          maxLength={9}
          onChange={(e) => {
            setSteForm({ ...steForm, siren: e.target.value });
            setSteErr({
              ...steErr,
              siren: '',
            });
          }}
          errorMessage={steErr.siren}
          onBlur={(e) =>
            setSteErr({
              ...steErr,
              siren:
                getEmptyError(e.target.value) ||
                getMinLenError(e.target.value, 9),
            })
          }
        />
        <div className="flex-column gap-s">
          <div className="flex-row gap-s">
            <p>Méthode d’authentification</p>
            <Tooltip
              message={
                <>
                  Locale = authentification portée par PING IDENTITY.
                  <br />
                  Fédérée = authentification portée par le partenaire.
                </>
              }
            />
          </div>

          <div className={`flex-row gap-m ${styles.choices}`}>
            {methods.map((method, index) => (
              <ChoiceButton
                type="button"
                key={index}
                onClick={(e) => {
                  setSteForm({ ...steForm, authn_method: method.value });
                }}
                selected={steForm.authn_method === method.value}
              >
                {method.label}
              </ChoiceButton>
            ))}
          </div>
        </div>
        <div className="flex-column gap-s">
          <p>Type *</p>
          <div className={`flex-row gap-m ${styles.choices}`}>
            {types.map((type, index) => (
              <ChoiceButton
                type="button"
                key={index}
                onClick={(e) => {
                  setSteForm({ ...steForm, employer_type: type.value });
                }}
                selected={steForm.employer_type === type.value}
              >
                {type.label}
              </ChoiceButton>
            ))}
          </div>
        </div>
        <div className="flex-column gap-s">
          <Input
            label="Tag *"
            maxLength={50}
            placeholder="lorem ispum"
            onChange={(e) => {
              const newValue = (e?.target?.value ?? '').replaceAll(
                /[^a-zA-Z.0-9 \-_&]/g,
                '',
              );
              setSteForm({ ...steForm, tag: newValue });
            }}
            errorMessage={steErr.tag}
            onBlur={(e) =>
              setSteErr({ ...steErr, tag: getEmptyError(e.target.value) })
            }
          />
          <p className="xs">
            *Mot-clé permettant d&apos;identifier la société. Mot-clé utilisé
            pour constituer l&apos;identifiant utilisateur d&apos;accès à nos
            applications.
          </p>
        </div>
        <Input
          label="ID de l’organisation API d’administration"
          placeholder="123456789"
          maxLength={50}
          onChange={(e) =>
            setSteForm({ ...steForm, api_organization_id: e.target.value })
          }
          helperText="Ce partenaire utilise l'API d'administration pour gérer ses employés. Renseignez ici l'identifiant technique utilisé par le Partenaire pour accéder à notre API. Identifiant donné par la DSI PF France."
        />
        <p className="xs">* Champs obligatoires</p>
        <Button
          icon={<PlusCircle />}
          type="button"
          variant="primary"
          size="medium"
          disabled={isDisabled}
          onClick={(e) => {
            setOpenedModal(true);
          }}
        >
          Créer une société
        </Button>
      </form>
      <Modal
        primaryBtnProps={{
          loading: isLoading,
          children: 'Confirmer',
          onClick: handleSubmit,
        }}
        secondaryBtnProps={{
          children: 'Annuler',
          onClick: () => setOpenedModal(false),
        }}
        title="Voulez-vous créer la société ?"
        open={openedModal}
        onClose={() => setOpenedModal(false)}
      />
    </div>
  );
}
